import React from 'react'; // Ensure React is imported if using React.Fragment explicitly
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Button } from '@/components/ui/button';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"; // Assuming shadcn/ui tooltip

interface MapViewProps {
  imageUrl: string;
  altText?: string;
}

const MapView: React.FC<MapViewProps> = ({ imageUrl, altText = 'Museum Floor Plan' }) => {
  const { t } = useTranslation();

  return (
    // Adjusted container: flex-grow to fill space, min-height as fallback
    <div className="relative w-full flex-grow min-h-[400px] overflow-hidden rounded-lg border bg-gray-100 dark:border-neutral-800 dark:bg-neutral-900">
      <TooltipProvider delayDuration={100}>
        <TransformWrapper
          initialScale={1}
        minScale={0.5}
        maxScale={5}
        limitToBounds={true}
        doubleClick={{ disabled: true }} // Disable double click zoom for smoother interaction
      >
        {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
          <React.Fragment>
            {/* Controls with Tooltips */}
            <div className="absolute top-3 right-3 z-10 flex flex-col gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => zoomIn()}
                    aria-label={t('map.zoomInAriaLabel', 'Zoom In')}
                    className="h-9 w-9 bg-background/80 backdrop-blur-sm hover:bg-background/95" // Slightly larger, better hover
                  >
                    <ZoomIn className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  <p>{t('map.zoomInTooltip', 'Zoom In')}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => zoomOut()}
                    aria-label={t('map.zoomOutAriaLabel', 'Zoom Out')}
                    className="h-9 w-9 bg-background/80 backdrop-blur-sm hover:bg-background/95" // Slightly larger, better hover
                  >
                    <ZoomOut className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  <p>{t('map.zoomOutTooltip', 'Zoom Out')}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => resetTransform()}
                    aria-label={t('map.resetViewAriaLabel', 'Reset View')}
                    className="h-9 w-9 bg-background/80 backdrop-blur-sm hover:bg-background/95" // Slightly larger, better hover
                  >
                    <RotateCcw className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  <p>{t('map.resetViewTooltip', 'Reset View')}</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Map Image */}
            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%' }}
              contentStyle={{ width: '100%', height: '100%' }}
            >
              <img
                src={imageUrl}
                alt={altText}
                className="h-full w-full object-contain" // Use object-contain to fit image within bounds
              />
            </TransformComponent>
          </React.Fragment>
        )}
      </TransformWrapper>
      </TooltipProvider>
    </div>
  );
};

export default MapView;
